import React, { useState, useEffect } from 'react';

function ProductsPage({ onAddToCart }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantities, setQuantities] = useState({});

  // Get IP from environment or use localhost
  const BACKEND_URL = 'https://dishamedicalstore-backend.onrender.com';
  
  // Fetch products from backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/products`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }

        const data = await response.json();
        setProducts(data);
        setFilteredProducts(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [BACKEND_URL]);

  // Handle search
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    filterProducts(term, selectedCategory);
  };

  // Handle category filter
  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    filterProducts(searchTerm, category);
  };

  // Filter products based on search and category
  const filterProducts = (search, category) => {
    let filtered = products;

    if (category !== 'All') {
      filtered = filtered.filter(p => p.category === category);
    }

    if (search) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(search) ||
        p.description.toLowerCase().includes(search) ||
        p.category.toLowerCase().includes(search)
      );
    }

    setFilteredProducts(filtered);
  };

  // Handle add to cart
  const handleAddToCart = (product) => {
    const productId = product._id;
    
    // Add to cart with quantity
    for (let i = 0; i < (quantities[productId] || 1); i++) {
      onAddToCart(product);
    }
    
    // Reset quantity and remove from cart UI
    setQuantities({ ...quantities, [productId]: 0 });
  };

  // Increase quantity
  const increaseQuantity = (productId) => {
    setQuantities({
      ...quantities,
      [productId]: (quantities[productId] || 0) + 1
    });
  };

  // Decrease quantity
  const decreaseQuantity = (productId) => {
    const currentQty = quantities[productId] || 0;
    if (currentQty > 1) {
      setQuantities({
        ...quantities,
        [productId]: currentQty - 1
      });
    } else {
      setQuantities({
        ...quantities,
        [productId]: 0
      });
    }
  };

  const categories = ['All', 'Baby Care', 'Oral Hygiene', 'Skin Care', 'Health Supplements'];

  return (
    <main className="products-page">
      <div className="products-header">
        <h2>Our Products</h2>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <input
          type="text"
          placeholder="🔍 Search products..."
          value={searchTerm}
          onChange={handleSearch}
          className="search-input"
        />
      </div>

      {/* Category Filter */}
      <div className="category-filter">
        <div className="filter-buttons">
          {categories.map(cat => (
            <button
              key={cat}
              className={`filter-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => handleCategoryFilter(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading && <p className="loading">⏳ Loading products...</p>}

      {/* Error State */}
      {error && <p className="error">❌ {error}</p>}

      {/* No Results */}
      {!loading && filteredProducts.length === 0 && (
        <p className="no-results">No products found. Try a different search or filter.</p>
      )}

      {/* Products Desktop Table View */}
      {!loading && filteredProducts.length > 0 && (
        <div className="products-list-container">
          <p className="results-count">📦 Showing {filteredProducts.length} products</p>
          
          <div className="products-list">
            {/* Header Row */}
            <div className="product-row header-row">
              <div className="col-name">Product Name</div>
              <div className="col-category">Category</div>
              <div className="col-price">Price</div>
              <div className="col-description">Description</div>
              <div className="col-action">Action</div>
            </div>

            {/* Product Rows */}
            {filteredProducts.map((product, idx) => (
              <div key={product._id || idx} className="product-row">
                <div className="col-name">
                  <strong>{product.name}</strong>
                </div>
                <div className="col-category">
                  <span className="category-badge">{product.category}</span>
                </div>
                <div className="col-price">
                  <span className="price">₹{product.price}</span>
                </div>
                <div className="col-description">
                  {product.description}
                </div>
                <div className="col-action">
                  {quantities[product._id] > 0 ? (
                    <div className="quantity-counter">
                      <button 
                        className="qty-btn minus"
                        onClick={() => decreaseQuantity(product._id)}
                      >
                        −
                      </button>
                      <span className="qty-display">{quantities[product._id]}</span>
                      <button 
                        className="qty-btn plus"
                        onClick={() => increaseQuantity(product._id)}
                      >
                        +
                      </button>
                      <button
                        className="btn-add-to-cart"
                        onClick={() => handleAddToCart(product)}
                      >
                        Add
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn-add-to-cart"
                      onClick={() => increaseQuantity(product._id)}
                    >
                      Add to Cart
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Card Grid */}
          <div className="products-mobile-grid">
            {filteredProducts.map((product, idx) => (
              <div key={product._id || idx} className="product-mobile-card">
                <div className="product-card-inner">
                  <div className="card-top">
                    <div className="product-header">
                      <h3>{product.name}</h3>
                      <span className="category-badge">{product.category}</span>
                    </div>
                  </div>
                  <div className="card-middle">
                    <p className="description">{product.description}</p>
                  </div>
                  <div className="card-bottom">
                    <div className="price-section">
                      <span className="price">₹{product.price}</span>
                    </div>
                    {quantities[product._id] > 0 ? (
                      <div className="quantity-counter-mobile">
                        <button 
                          className="qty-btn-mobile minus"
                          onClick={() => decreaseQuantity(product._id)}
                        >
                          −
                        </button>
                        <span className="qty-display-mobile">{quantities[product._id]}</span>
                        <button 
                          className="qty-btn-mobile plus"
                          onClick={() => increaseQuantity(product._id)}
                        >
                          +
                        </button>
                        {/* ADDED: Mobile Add to Cart Button */}
                        <button
                          className="btn-add-mobile"
                          onClick={() => handleAddToCart(product)}
                        >
                          Add
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn-add-to-cart-mobile"
                        onClick={() => increaseQuantity(product._id)}
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}

export default ProductsPage;